/**
 * Deletion planning for the collections surface — **O13** (cascade is an
 * argument the model must supply) and **O14** (children before parents).
 *
 * ## O14 on THIS surface means something different, and pretending otherwise
 * would be a lie
 *
 * On boards, "children before parents" is a DURABILITY property: a board
 * delete is N separate `extension_records_delete` transactions, so a failure
 * half-way through leaves whatever the loop already tombstoned. Deleting cards
 * before their list means a partial failure leaves a visible list with fewer
 * cards; the other order leaves cards whose `parent_id` names a list that no
 * longer exists — synced, counting against the row cap, reachable from
 * nothing.
 *
 * **Here there is exactly one write and it cannot half-fail.** The whole
 * collections array is handed to `setCollections` and persisted as one
 * snapshot. So a naive "delete children first" would be pure ceremony: the
 * final array is the same whichever order the removals were computed in, and a
 * test asserting the order would be asserting a comment.
 *
 * What IS real here — and what O14 is actually protecting against on this
 * surface — is a cascade plan that removes a PARENT while leaving a child
 * behind. The child does not vanish; it becomes an ORPHAN in the stored tree:
 * a request whose `folder_id` names a folder that is gone renders in no folder
 * at all, and a folder whose `parent_id` is gone renders nowhere. That is the
 * same failure the boards ordering exists to prevent, reached by a wrong plan
 * instead of by a wrong moment.
 *
 * So the ordering is enforced as an INVARIANT OVER EVERY INTERMEDIATE STATE:
 *
 *     `applyDeletionPlan` applies the plan's steps one at a time and, after
 *     each step, requires that the working tree has gained no orphan.
 *
 * Children-first satisfies that at every step by construction. Parents-first
 * violates it at the very first folder removal. A plan that forgets a nested
 * subfolder's requests violates it too — which is the bug a hand-written
 * cascade actually ships. The invariant is therefore load-bearing rather than
 * decorative, it is ablatable (reverse the plan and it goes red), and it says
 * what it means.
 *
 * ## The plan is also what the REFUSAL shows
 *
 * **O13:** above one node, omitting `cascade: true` returns a LISTING of what
 * would be deleted rather than a deletion. The listing is this same plan,
 * which means the thing the model is shown and the thing that would run are
 * the same object rather than two descriptions that can drift.
 */
import { collectFolderSubtreeIds, getFolders, normalizeParentId } from '../utils/collectionFolders';

/**
 * Every node a deletion would remove, DEEPEST FIRST.
 *
 * `kind` is one of `request` | `folder` | `collection`. The array is the
 * execution order and the listing order, and both halves of that matter — see
 * the header.
 */
export function planFolderDelete(collection, folderId) {
  const folders = getFolders(collection);
  const subtree = collectFolderSubtreeIds(folders, folderId);
  const inSubtree = folders.filter((f) => subtree.has(String(f.id)));

  const requests = (collection.requests || []).filter((r) =>
    subtree.has(String(normalizeParentId(r.folder_id)))
  );

  // Depth from the deletion root, so the deepest folder is removed first and
  // the named folder last. Computed against the ORIGINAL tree — a depth read
  // off a tree that is already being mutated is the bug this whole module is
  // about.
  const depthOf = (folder) => {
    const byId = new Map(folders.map((f) => [String(f.id), f]));
    let depth = 0;
    let current = folder;
    const seen = new Set();
    while (current && !seen.has(String(current.id))) {
      seen.add(String(current.id));
      const parentId = normalizeParentId(current.parent_id);
      if (!parentId || !subtree.has(String(parentId))) break;
      current = byId.get(String(parentId));
      depth += 1;
    }
    return depth;
  };

  const folderSteps = inSubtree
    .slice()
    .sort((a, b) => depthOf(b) - depthOf(a))
    .map((f) => ({ kind: 'folder', id: String(f.id), name: String(f.name || '') }));

  return [
    ...requests.map((r) => ({
      kind: 'request',
      id: String(r.id),
      name: String(r.name || ''),
      method: String(r.method || 'GET').toUpperCase(),
    })),
    ...folderSteps,
  ];
}

/** Every request, then every folder deepest-first, then the collection head. */
export function planCollectionDelete(collection) {
  const folders = getFolders(collection);
  const roots = folders.filter((f) => !normalizeParentId(f.parent_id));

  // Reuse the folder plan per root, so there is ONE depth-ordering rule rather
  // than two that can disagree.
  const seen = new Set();
  const folderSteps = [];
  for (const root of roots) {
    for (const step of planFolderDelete(collection, root.id)) {
      if (step.kind !== 'folder' || seen.has(step.id)) continue;
      seen.add(step.id);
      folderSteps.push(step);
    }
  }
  // A folder whose `parent_id` names a folder that does not exist is already
  // an orphan in the stored tree; it belongs to no root, so the loop above
  // never reaches it. Append it with the rest of the folders rather than
  // leaving it behind — the deletion must be complete even when the tree it
  // starts from is not.
  for (const f of folders) {
    if (!seen.has(String(f.id))) {
      seen.add(String(f.id));
      folderSteps.push({ kind: 'folder', id: String(f.id), name: String(f.name || '') });
    }
  }

  return [
    ...(collection.requests || []).map((r) => ({
      kind: 'request',
      id: String(r.id),
      name: String(r.name || ''),
      method: String(r.method || 'GET').toUpperCase(),
    })),
    ...folderSteps,
    { kind: 'collection', id: String(collection.id), name: String(collection.name || '') },
  ];
}

/**
 * Nodes in `collection` whose parent does not exist, as `kind:id` strings.
 *
 * Used as a DIFFERENCE against the state before the plan ran, never as an
 * absolute check: a workspace can already contain an orphan (an interrupted
 * sync, a record written by an older version), and refusing every delete in
 * such a workspace would be a new failure rather than a guard.
 */
export function orphansIn(collection) {
  if (!collection) return [];
  const folderIds = new Set(getFolders(collection).map((f) => String(f.id)));
  const out = [];
  for (const f of getFolders(collection)) {
    const parentId = normalizeParentId(f.parent_id);
    if (parentId && !folderIds.has(String(parentId))) out.push(`folder:${f.id}`);
  }
  for (const r of collection.requests || []) {
    const folderId = normalizeParentId(r.folder_id);
    if (folderId && !folderIds.has(String(folderId))) out.push(`request:${r.id}`);
  }
  return out;
}

/** One step, applied to a working copy. `null` collection = the head is gone. */
function applyStep(collection, step) {
  if (step.kind === 'collection') return null;
  if (step.kind === 'request') {
    return {
      ...collection,
      requests: (collection.requests || []).filter((r) => String(r.id) !== step.id),
    };
  }
  const folders = getFolders(collection).filter((f) => String(f.id) !== step.id);
  return { ...collection, settings: { ...(collection.settings || {}), folders } };
}

/**
 * Run the plan. Returns `{ collection, removed }` — `collection` is `null` when
 * the plan deleted the head — or `{ error }` naming the invariant that broke.
 *
 * The invariant is checked after EVERY step, not only at the end, and that is
 * the whole point: the end state of a reversed plan is identical to the end
 * state of a correct one, so an end-state check would pass for both. See the
 * header.
 */
export function applyDeletionPlan(collection, plan) {
  const before = new Set(orphansIn(collection));
  let working = collection;
  const removed = [];
  for (const step of plan) {
    if (working === null) {
      return { error: `the plan continues past the collection head at ${step.kind}:${step.id}` };
    }
    working = applyStep(working, step);
    removed.push(step);
    if (working === null) continue;
    const now = orphansIn(working).filter((o) => !before.has(o));
    if (now.length) {
      return {
        error: `removing ${step.kind}:${step.id} orphaned ${now.join(', ')} — children must be removed before their parent`,
      };
    }
  }
  return { collection: working, removed };
}

/** `{requests, folders}` counts for a refusal's listing and a result's summary. */
export function countsOf(plan) {
  return {
    requests: plan.filter((s) => s.kind === 'request').length,
    folders: plan.filter((s) => s.kind === 'folder').length,
  };
}
